/**
 * Before and After Custom Element (<before-after>)
 * 
 * Accessible, mobile-first image comparison slider.
 * Synchronizes native range input with hardware-accelerated clip-path masking,
 * handles pointer/touch events, and updates ARIA live values for screen readers.
 */

if (!customElements.get('before-after')) {
  class BeforeAfter extends HTMLElement {
    constructor() {
      super();
      this.range = null;
      this.stage = null;
      this.beforeLabel = 'Before';
      this.afterLabel = 'After';
      this.isDragging = false;

      this.onRangeInput = this.onRangeInput.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
    }

    connectedCallback() {
      this.range = this.querySelector('.before-after__range');
      this.stage = this.querySelector('.before-after__stage');
      this.beforeLabel = this.getAttribute('data-before-label') || 'Before';
      this.afterLabel = this.getAttribute('data-after-label') || 'After';

      if (!this.range || !this.stage) return;

      // Initialize position from range value or attribute
      const initialPos = this.range.value || this.getAttribute('data-initial-pos') || 50;
      this.setPosition(initialPos);

      // Listen to range input events (handles keyboard and standard slider interactions)
      this.range.addEventListener('input', this.onRangeInput);

      // Listen to direct pointer interactions on the visual stage for seamless dragging
      this.stage.addEventListener('pointerdown', this.onPointerDown);
    }

    disconnectedCallback() {
      if (this.range) {
        this.range.removeEventListener('input', this.onRangeInput);
      }
      if (this.stage) {
        this.stage.removeEventListener('pointerdown', this.onPointerDown);
      }
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    onRangeInput(event) {
      this.setPosition(event.target.value);
    }

    onPointerDown(event) {
      // Allow standard link/button clicks if any are inside
      if (event.target.closest('a, button')) return;

      this.isDragging = true;
      this.stage.classList.add('is-dragging');
      this.updatePositionFromPointer(event);

      window.addEventListener('pointermove', this.onPointerMove, { passive: true });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.isDragging) return;
      this.updatePositionFromPointer(event);
    }

    onPointerUp() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.stage.classList.remove('is-dragging');

      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    updatePositionFromPointer(event) {
      const rect = this.stage.getBoundingClientRect();
      if (!rect.width) return;

      const clientX = event.clientX !== undefined ? event.clientX : (event.touches && event.touches[0] ? event.touches[0].clientX : 0);
      let percentage = ((clientX - rect.left) / rect.width) * 100;
      percentage = Math.max(0, Math.min(100, percentage));

      if (this.range) {
        this.range.value = percentage;
      }
      this.setPosition(percentage);
    }

    setPosition(value) {
      const numericVal = parseFloat(value);
      const clampedVal = isNaN(numericVal) ? 50 : Math.max(0, Math.min(100, numericVal));

      // Update CSS custom property for hardware-accelerated clip-path and divider position
      this.style.setProperty('--slider-pos', `${clampedVal}%`);

      // Update ARIA values for assistive technology
      if (this.range) {
        this.range.setAttribute('aria-valuenow', clampedVal.toFixed(1));
        this.range.setAttribute(
          'aria-valuetext',
          `${Math.round(clampedVal)}% ${this.beforeLabel} visible, ${Math.round(100 - clampedVal)}% ${this.afterLabel} visible`
        );
      }
    }
  }

  customElements.define('before-after', BeforeAfter);
}
